<template>    
    <nav v-if="shouldShowPagination">
        <div class="pagination-wrapper ly">
            <div class="pagination">
                <button class="prevNextButtons" :disabled="currentPage == 1" @click="controls(-1)">
                    Anterior
                </button>
                <button v-for="(page, index) in pagesToShow" :key="index" @click="changePage(page)"
                    :class="{ active: page === value }">
                    {{ page }}
                </button>
                <button class="prevNextButtons" :disabled="(currentPage === totalPages) || (totalPages == 0)" @click="controls(1)">
                    Siguiente
                </button>
            </div>
            <v-select
                v-if="showPerPageSelector"
                class="pagination-select ml-3"
                :items="paginateByOptions"
                v-model="itemsPerPageModel"
                @change="updatePerPage"
                dense
                hide-details
                outlined
                color="bluePrimary"
                :menu-props="{ offsetY: true, contentClass: 'pagination-menu' }"
            ></v-select>
        </div>
    </nav>
</template>

<script>
export default {
    name: 'AppCustomPaginationComponent',
    props: {
        value: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        totalVisible: {
            type: Number,
            default: 3,
        },
        itemsPerPage: {
            type: Number,
            default: 10,
        },
        perPage: {
            type: Number,
            default: null,
        },
        paginateBy: {
            type: Array,
            default: () => [5, 10, 20, 50, 100],
        },
        showPerPageSelector: {
            type: Boolean,
            default: true,
        },
        totalRegistros: {
            type: Number,
            default: null,
        },
    },
    emits: ["update:modelValue", "update:itemsPerPage", "update:perPage", "per-page-changed", "input", "page-changed", "change"],
    data: () => {
        return {
        };
    },
    computed: {
        shouldShowPagination() {
            const hasValidTotalPages = this.totalPages > 0;
            const hasValidTotalRegistros = this.totalRegistros !== null && 
                                            this.totalRegistros !== undefined && 
                                            !isNaN(this.totalRegistros) &&
                                            this.totalRegistros > 0;
            
            return hasValidTotalPages || hasValidTotalRegistros;
        },
        currentPage: {
            get() {
                return this.value;
            },
            set(newPage) {
                this.$emit("input", newPage);
            },
        },
        itemsPerPageModel: {
            get() {
                return this.perPage !== null ? this.perPage : this.itemsPerPage;
            },
            set(value) {
                this.$emit("update:itemsPerPage", value);
                this.$emit("update:perPage", value);
            },
        },
        paginateByOptions() {
            const currentPerPage = this.perPage !== null ? this.perPage : this.itemsPerPage;
            const options = Array.isArray(this.paginateBy) ? [...this.paginateBy] : [];
            if (!options.includes(currentPerPage)) {
                options.push(currentPerPage);
            }
            return options
                .filter((option) => typeof option === "number" && option > 0)
                .sort((a, b) => a - b);
        },
        pagesToShow() {
            const currentPage = this.value;
            const total = this.totalPages;
            const totalVisible = this.totalVisible;
            const halfVisible = Math.floor(totalVisible / 2);
            let from = currentPage - halfVisible;
            let to = currentPage + halfVisible;

            // Asegurar que las páginas mostradas estén dentro del rango válido
            if (from < 1) {
                from = 1;
                to = Math.min(totalVisible, total);
            }
            if (to > total) {
                to = total;
                from = Math.max(1, total - totalVisible + 1);
            }

            // Crear un array con las páginas que se mostrarán

            const pages = [];
            if (from > 1) {
                pages.push(1);
                if (from > 2) {
                    pages.push("..."); // Agregar puntos suspensivos al principio o al final
                }
            }
            for (let i = from; i <= to; i++) {
                pages.push(i);
            }
            if (to < total) {
                if (to < total - 1) {
                    pages.push("..."); // Agregar puntos suspensivos  al final
                }
                pages.push(total);
            }
            return pages;
        },
    },
    watch: {
        value(newValue, oldValue) {
            this.$emit("change", newValue, oldValue);
        },
    },
    methods: {
        changePage(page) {
            if (page === "...") return;
            if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
                this.currentPage = page;
                this.$emit("page-changed", page);
            }
        },
        controls(variable) {
            const newPage = this.currentPage + variable;
            if (newPage >= 1 && newPage <= this.totalPages) {
                this.currentPage = newPage;
                this.$emit("page-changed", newPage);
            }
        },
        updatePerPage(newValue) {
            const parsedValue = typeof newValue === "number" ? newValue : Number(newValue);
            const perPageValue = !isNaN(parsedValue) ? parsedValue : this.itemsPerPageModel;
            if (this.value > 1) {
                this.currentPage = 1;
            }
            this.$emit("per-page-changed", perPageValue);
            this.$emit("change");
        },
    },
};
</script>

<style scoped>
/* Estilos opcionales para dar formato a la paginación */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
}

.pagination button {
    color: #1C1E4D;
    background-color: transparent;
    border: none;
    cursor: pointer;
    padding: 3px 7px;
    margin: 0 5px;
    border-radius: 10px !important;
    box-shadow: 0 2px 2px rgb(213, 211, 211);
}

.prevNextButtons{
    box-shadow: none !important;
}

.pagination button:hover {
    background-color: #1C1E4D;
    border-radius: 5px;
    color: #fff;
}

.pagination button.active {
    background-color: #1C1E4D;
    border-radius: 5px;
    color: #fff;
    cursor: default;
}

.pagination button:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.pagination-select {
    max-width: 85px !important;
}

::v-deep .pagination-select .v-input__control {
    min-height: 44px;
}

::v-deep .pagination-select .v-input__slot {
    border-radius: 10px !important;
    padding: 0 10px !important;
    box-shadow: none !important;
    background-color: #ffffff !important;
    transition: border-color 0.2s ease;
}

::v-deep .pagination-select.v-text-field--outlined fieldset {
    border: 2px solid #d0d0d0 !important;
}

::v-deep .pagination-select.v-text-field--outlined:hover fieldset {
    border: 2px solid #b0b0b0 !important;
}

::v-deep .pagination-select.v-text-field--outlined.v-input--is-focused fieldset {
    border: 2px solid #1C1E4D !important;
}

::v-deep .pagination-select.v-input--is-focused fieldset {
    border: 2px solid #1C1E4D !important;
}

::v-deep .pagination-menu {
    border: 2px solid #313945;
    border-radius: 12px;
    box-shadow: 0px 8px 20px rgba(16, 24, 40, 0.12);
}

::v-deep .pagination-menu .v-list-item {
    min-height: 36px;
}
</style>